
/**
 * Fixes Complianz GDPR document links that are empty or placeholders.
 */
const fixComplianzGdpr = () => {
    if( ! document.querySelector( '.cmplz-documents' ) ) {
        // No Complianz GDPR documents found.
        return;
    }

    document.querySelectorAll( '.cmplz-documents a' )?.forEach( link  => {
        const { textContent, href } = link;

        if ( ! textContent
             || '' === textContent.trim()
             || '{title}' === textContent.trim()
             || ! href
             || href.endsWith( '/{document_id}' )
             || '#' === href.trim()
        ) {
            // Broken link or empty label, causing a11y issues. Remove it from the DOM.
            link.parentNode.remove();
        }
    } );
};

/**
 * WCAG 1.4.1 (Use of Color) — The Complianz consent toggles communicate their
 * on/off state only through background color and the knob position.
 *
 * Sighted users are handled entirely in CSS: a ::before pseudo-element on
 * .cmplz-category-title uses :has(.cmplz-consent-checkbox:checked) to swap a
 * ✓ / ✕ icon reactively, with no JS sync problems.
 *
 * Assistive tech users are handled here: we rewrite the existing
 * .screen-reader-text inside the checkbox's <label> to include the category
 * name plus a meaningful state ("Statistieken: Geaccepteerd" /
 * "Statistieken: Geweigerd"). Because that span is the checkbox's accessible
 * name, the announcement becomes "Statistieken: Geaccepteerd, selectievakje,
 * aangevinkt" instead of the generic "Statistieken, selectievakje,
 * aangevinkt".
 */
const {on, off} = window.gkt_complianz.labels || { on: 'Geaccepteerd', off: 'Geweigerd' };
const NEW_WINDOW_LABEL = window.gkt_complianz?.labels?.newWindow || 'opent in nieuw venster';
const CHECKBOX_STYLE = window.gkt_complianz?.checkbox_style || 'slider';
const CHECKBOX_STYLE_CLASS = `cmplz-checkbox-style-${CHECKBOX_STYLE}`;

// English strings Complianz ships untranslated in some locales. Wrapping them
// in <span lang="en"> makes Dutch-voice screen readers pronounce them with
// English phonetics instead of mangling them (WCAG 3.1.2 Language of Parts).
const ENGLISH_STRINGS = [
    'Always active',
    'Manage options',
    'Manage services',
    'Read more about these purposes',
];

const getComplianzCategoryName = ( wrapper ) => {
    const header = wrapper.closest( '.cmplz-category-header' );
    const title  = header?.querySelector( '.cmplz-category-title' );
    return title?.textContent?.trim() || '';
};

/**
 * Click + keydown events on the consent toggle bubble up to <summary> and
 * trigger its default "toggle details" activation behavior, so a single click
 * ends up both changing consent AND opening/closing the category panel. Stop
 * the event at the wrapper so summary's activation behavior never runs.
 *
 * The keydown handler also makes Enter toggle the checkbox — native checkboxes
 * only respond to Space, but Complianz styles these as switches and users
 * reasonably expect Enter to work on a switch-shaped control.
 *
 * Listener is attached once per wrapper, tracked via a data attribute.
 */
const preventSummaryActivation = ( wrapper ) => {
    if ( wrapper.dataset.gkCmplzStopBubble === '1' ) {
        return;
    }
    wrapper.dataset.gkCmplzStopBubble = '1';

    wrapper.addEventListener( 'click', ( event ) => event.stopPropagation() );

    wrapper.addEventListener( 'keydown', ( event ) => {
        const checkbox = event.target.closest?.( '.cmplz-consent-checkbox' );
        if ( checkbox && event.key === 'Enter' ) {
            event.preventDefault();
            checkbox.click();
        }
        event.stopPropagation();
    } );
};

const updateComplianzToggleLabel = ( checkbox ) => {
    const wrapper = checkbox.closest( '.cmplz-banner-checkbox' );
    if ( ! wrapper ) {
        return;
    }

    preventSummaryActivation( wrapper );

    // A disabled checkbox OR an always-active wrapper represents a functional
    // (always-on) category. Complianz doesn't always mark it disabled, so we
    // also match the .cmplz-always-active ancestor.
    const isOn      = checkbox.checked || checkbox.disabled || !! wrapper.closest( '.cmplz-always-active' );
    const stateLabel = isOn ? on : off;

    const label = wrapper.querySelector( 'label.cmplz-label' );
    if ( ! label ) {
        return;
    }

    if ( CHECKBOX_STYLE === 'slider' ) {
        // Slider variant: state is conveyed visually by color + knob position
        // only, so we put "Category: Geaccepteerd/Geweigerd" into the
        // screen-reader-text span that backs the checkbox's accessible name.
        const srLabel = label.querySelector( '.screen-reader-text' );
        if ( srLabel ) {
            const categoryName = getComplianzCategoryName( wrapper );
            srLabel.textContent = categoryName ? `${categoryName}: ${stateLabel}` : stateLabel;
        }
        return;
    }

    // Classic variant: expose the state label as visible text inside
    // .cmplz-label. The category name is already visible next to the checkbox
    // via .cmplz-category-title, and the native checkbox announces its own
    // checked/unchecked state, so we only need to surface "Geaccepteerd" /
    // "Geweigerd" as the label text. Replace any Complianz-provided
    // .screen-reader-text span so the label contains only our state text.
    label.replaceChildren( document.createTextNode( stateLabel ) );
};

const enhanceComplianzToggles = ( root = document ) => {
    root.querySelectorAll?.( '.cmplz-banner-checkbox .cmplz-consent-checkbox' )
        .forEach( updateComplianzToggleLabel );
};

/**
 * Wrap untranslated English strings in <span lang="en"> so SR voices switch
 * pronunciation accordingly. Idempotent: once a text node is wrapped, it is
 * no longer a direct text child and won't be re-processed.
 */
const wrapComplianzEnglishStrings = ( scope ) => {
    scope.querySelectorAll( '.cmplz-cookiebanner a, .cmplz-cookiebanner .cmplz-always-active' )
        .forEach( el => {
            Array.from( el.childNodes ).forEach( node => {
                if ( node.nodeType !== Node.TEXT_NODE ) {
                    return;
                }
                if ( ! ENGLISH_STRINGS.includes( node.textContent.trim() ) ) {
                    return;
                }
                const span = document.createElement( 'span' );
                span.lang = 'en';
                span.textContent = node.textContent;
                node.replaceWith( span );
            } );
        } );
};

/**
 * Patch the a11y issues in Complianz's own banner markup. Each fix is
 * idempotent so it is safe to call repeatedly from the MutationObserver.
 */
const fixComplianzMinorA11y = ( root = document ) => {
    const scope = root.querySelectorAll ? root : document;

    // Tag the banner with the configured Complianz checkbox style so CSS can
    // target slider vs classic checkbox variants without re-reading settings.
    scope.querySelectorAll( '.cmplz-cookiebanner' )
        .forEach( banner => banner.classList.add( CHECKBOX_STYLE_CLASS ) );

    // The functional category is always on and cannot be toggled off, but
    // Complianz ships its checkbox as a regular enabled control. Mark it
    // disabled so both sighted and AT users get consistent "you can't change
    // this" feedback, and so the :disabled selector used in the slider CSS
    // lights up the ✓ icon reliably.
    scope.querySelectorAll( '.cmplz-always-active .cmplz-consent-checkbox:not([disabled])' )
        .forEach( cb => {
            cb.disabled = true;
            cb.setAttribute( 'aria-disabled', 'true' );
        } );

    // Dialog must not be a live region. role="dialog" + aria-modal already
    // handles focus announcements; aria-live causes the whole banner to be
    // re-announced on every mutation.
    scope.querySelectorAll( '.cmplz-cookiebanner[aria-live]' )
        .forEach( dialog => dialog.removeAttribute( 'aria-live' ) );

    // Dialog title should be a proper heading (<h2>) so SR heading lists pick
    // it up and landmark/heading structure is meaningful. Preserve the id
    // that aria-labelledby points to.
    scope.querySelectorAll( '.cmplz-cookiebanner .cmplz-title' )
        .forEach( title => {
            if ( title.tagName === 'H2' ) {
                return;
            }
            const h2 = document.createElement( 'h2' );
            for ( const { name, value } of title.attributes ) {
                h2.setAttribute( name, value );
            }
            h2.append( ...title.childNodes );
            title.replaceWith( h2 );
        } );

    // Close control is a <div role="button"> — keep it in place (Complianz may
    // have bound its click handler directly to the element, so swapping would
    // break that) but ensure Enter/Space actually activate it like a real
    // <button> would. Also remove the now-unnecessary role="button" so screen
    // readers announce it once, consistently.
    scope.querySelectorAll( '.cmplz-cookiebanner .cmplz-close' )
        .forEach( closeEl => {
            if ( closeEl.dataset.gkCmplzKeyboard === '1' ) {
                return;
            }
            closeEl.dataset.gkCmplzKeyboard = '1';
            closeEl.addEventListener( 'keydown', ( event ) => {
                if ( event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar' ) {
                    event.preventDefault();
                    closeEl.click();
                }
            } );
        } );

    // WCAG 3.1.2 Language of Parts — wrap the hard-coded English strings.
    wrapComplianzEnglishStrings( scope );

    // Close icon — conflicting role="img" + aria-hidden="true". aria-hidden
    // wins, so role="img" is dead code and only confuses SR heuristics.
    scope.querySelectorAll( '.cmplz-cookiebanner .cmplz-close svg[role="img"]' )
        .forEach( svg => svg.removeAttribute( 'role' ) );

    // Decorative chevrons next to the category title need aria-hidden.
    scope.querySelectorAll( '.cmplz-cookiebanner .cmplz-icon svg:not([aria-hidden="true"])' )
        .forEach( svg => {
            svg.setAttribute( 'aria-hidden', 'true' );
            svg.setAttribute( 'focusable', 'false' );
        } );

    // size="40" has no effect on checkboxes — strip it.
    scope.querySelectorAll( '.cmplz-cookiebanner .cmplz-consent-checkbox[size]' )
        .forEach( cb => cb.removeAttribute( 'size' ) );

    // <summary> is natively focusable; the stray tabindex="0" on the first
    // category is redundant and creates an inconsistent focus pattern.
    scope.querySelectorAll( '.cmplz-cookiebanner summary[tabindex="0"]' )
        .forEach( s => s.removeAttribute( 'tabindex' ) );

    // <button> without an explicit type defaults to "submit", which submits
    // any surrounding form. Make all banner buttons explicit.
    scope.querySelectorAll( '.cmplz-cookiebanner button:not([type])' )
        .forEach( b => b.setAttribute( 'type', 'button' ) );

    // WCAG 2.5.3 Label in Name + 3.2.5 — the external TCF link's aria-label
    // diverges from its visible text. Rebuild the accessible name so it
    // starts with the visible text and adds the "new window" hint.
    scope.querySelectorAll( '.cmplz-cookiebanner a[target="_blank"]' )
        .forEach( link => {
            const visible = link.textContent.trim();
            if ( ! visible ) {
                return;
            }
            const expected = `${visible} (${NEW_WINDOW_LABEL})`;
            if ( link.getAttribute( 'aria-label' ) !== expected ) {
                link.setAttribute( 'aria-label', expected );
            }
        } );
};

const watchComplianzToggles = () => {
    // Initial pass for checkboxes already in the DOM.
    enhanceComplianzToggles();
    fixComplianzMinorA11y();

    // Complianz manages consent via its own click handlers and sets
    // checkbox.checked programmatically, which does NOT fire a `change` event.
    // We listen to three things to stay in sync:
    //
    //   1. Complianz's own `cmplz_status_change` custom event (authoritative).
    //   2. A delegated `change` listener for the rare case Complianz leaves
    //      the native event flow intact.
    //   3. A delegated `click` listener that re-syncs on the next frame, as a
    //      safety net for any path the first two miss.
    document.addEventListener( 'cmplz_status_change', enhanceComplianzToggles );

    document.addEventListener( 'change', ( event ) => {
        if ( event.target.matches?.( '.cmplz-banner-checkbox .cmplz-consent-checkbox' ) ) {
            updateComplianzToggleLabel( event.target );
        }
    } );

    document.addEventListener( 'click', ( event ) => {
        if ( ! event.target.closest?.( '.cmplz-category-header, .cmplz-banner-checkbox' ) ) {
            return;
        }
        requestAnimationFrame( () => enhanceComplianzToggles() );
    } );

    // The cookie banner is injected asynchronously; watch for it to appear.
    const observer = new MutationObserver( ( mutations ) => {
        mutations.forEach( ( mutation ) => {
            mutation.addedNodes.forEach( ( node ) => {
                if ( node.nodeType !== Node.ELEMENT_NODE ) {
                    return;
                }
                if ( node.matches?.( '.cmplz-banner-checkbox .cmplz-consent-checkbox' ) ) {
                    updateComplianzToggleLabel( node );
                } else {
                    enhanceComplianzToggles( node );
                    fixComplianzMinorA11y( node );
                }
            } );
        } );
    } );

    observer.observe( document.body, { childList: true, subtree: true } );
};

document.addEventListener( 'readystatechange', () => {
    if ( document.readyState === 'interactive' ) {
        fixComplianzGdpr();
        watchComplianzToggles();
    }
} );
